import { useState, useEffect, useMemo, useRef } from 'react';
import type { MouseEvent } from 'react';
import { SkeletonList } from '../../components/ui/skeletons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { sitesService } from '../../services/sitesService';
import {
  scheduleService,
  type MyAccessResponse,
} from '../../services/scheduleService';
import { useAuthStore } from '../../stores/authStore';
import { searchRowState } from './searchRows';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { siteSubtitle } from './siteLabel';
import { readSitesMemory, writeSitesMemory } from './sitesBackMemory';
import {
  MapPin,
  Search,
  Play,
  ChevronRight,
  CalendarPlus,
  Plus,
  Users,
  AlertTriangle,
  CloudRain,
} from 'lucide-react';
import { format, addDays, startOfWeek } from 'date-fns';
import { useToast } from '@/hooks/useToast';
import { LiteSiteCard } from './components/LiteSiteCard';
import { LiteListSection } from './components/LiteListSection';
import { DirectionsLink } from '../../components/DirectionsLink';

const PAGE_SIZE = 50;

function getWeekDays(): { label: string; date: string }[] {
  const monday = startOfWeek(new Date(), { weekStartsOn: 1 });
  // Mon–Sun since JIM-1030-08 (weekend days on the schedule).
  return Array.from({ length: 7 }, (_, i) => {
    const d = addDays(monday, i);
    return { label: format(d, 'EEE'), date: format(d, 'yyyy-MM-dd') };
  });
}

const TransitionChip = ({ phase }: { phase?: string | null }) =>
  phase === 'transitioning' ? (
    <span className="px-1.5 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-700 text-[10px] font-bold uppercase flex-shrink-0">
      Transitioning
    </span>
  ) : null;

export function LiteSitesPage() {
  // JIM-1027-24: coming back from a site lands where the inspector left —
  // same search, same page, same scroll position (read once, on mount).
  const remembered = useMemo(() => readSitesMemory(), []);
  const [searchTerm, setSearchTerm] = useState(remembered?.search ?? '');
  const [currentPage, setCurrentPage] = useState(remembered?.page ?? 1);
  const [dayPickerFor, setDayPickerFor] = useState<string | null>(null);
  const { user } = useAuthStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // One sites query per pause in typing, not one per keystroke: on prod each
  // keystroke was a full five-path sites query, and fourteen of them in
  // flight at once took ~55 s each (2026-10-19).
  const debouncedSearch = useDebouncedValue(searchTerm);
  const settling = searchTerm !== debouncedSearch;

  // Back to page 1 when the search changes — not on mount, where the
  // remembered page would be thrown away.
  const lastSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    if (lastSearchRef.current === debouncedSearch) return;
    lastSearchRef.current = debouncedSearch;
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Remember the search and page as they change (not on mount, which would
  // overwrite the remembered position with the top of the page) — a changed
  // search or page is a fresh list, so its remembered position is the top —
  // and the real position at the tap that leaves the list. An unmount cleanup
  // is too late: by then the router has scrolled the next page to the top,
  // and that scroll event has already reached any listener here (10/29).
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    writeSitesMemory({ search: searchTerm, page: currentPage, scrollY: 0 });
  }, [searchTerm, currentPage]);
  const rememberOnLeave = (e: MouseEvent<HTMLDivElement>) => {
    if (!(e.target as HTMLElement).closest('a[href]')) return;
    writeSitesMemory({
      search: searchTerm,
      page: currentPage,
      scrollY: window.scrollY,
    });
  };

  // Jim 10/27: the search covers every active site (staff-wide), not only the
  // ones on the inspector's list; a row outside their reach offers Claim first.
  const { data: sitesData, isLoading } = useQuery({
    queryKey: ['lite-sites', { search: debouncedSearch, page: currentPage }],
    queryFn: () =>
      sitesService.searchAllSites(debouncedSearch, currentPage, PAGE_SIZE),
    enabled: debouncedSearch.trim().length >= 2, // Only query when searching
  });

  // My Sites (assigned to me) + Group Sites (in my groups, claimable) — both
  // come from the single access resolver (/sites/my-access) so this view can
  // never drift from the schedule / dashboard / admin audit.
  const { data: access, isLoading: accessLoading } = useQuery({
    queryKey: ['my-access'],
    queryFn: () => scheduleService.getMyAccess(),
  });
  const mySites = access?.my_sites ?? [];
  const groupSites = access?.group_sites ?? [];
  const overdueSites = access?.overdue ?? [];
  const rainfallSites = access?.rainfall ?? [];

  // Deep-link from the dashboard cards: /lite/sites#overdue etc. scrolls to
  // that section once the data (and therefore the anchor) has rendered.
  const location = useLocation();
  useEffect(() => {
    if (!location.hash || accessLoading || searchTerm) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash, accessLoading, searchTerm]);

  // JIM-1027-24: once what was on screen is on screen again, put the list
  // back where it was. Once per visit; a dashboard deep link (#overdue) wins.
  const restoredRef = useRef(false);
  const listReady = searchTerm
    ? !settling &&
      !isLoading &&
      (sitesData !== undefined || debouncedSearch.trim().length < 2)
    : !accessLoading;
  useEffect(() => {
    if (restoredRef.current || !remembered || remembered.scrollY <= 0) return;
    if (!listReady || location.hash) return;
    restoredRef.current = true;
    const top = remembered.scrollY;
    requestAnimationFrame(() => window.scrollTo({ top }));
  }, [remembered, listReady, location.hash]);

  const addToPlanMutation = useMutation({
    mutationFn: ({ siteId, date }: { siteId: string; date: string }) =>
      scheduleService.addToPlan(siteId, date),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['weekly-plan'] });
      setDayPickerFor(null);
      const dayLabel = getWeekDays().find((d) => d.date === vars.date)?.label;
      toast({ title: `Added to ${dayLabel || vars.date}` });
    },
    onError: (error: any) => {
      toast({
        title: error?.response?.data?.detail || 'Failed to add to schedule',
        variant: 'destructive',
      });
    },
  });

  // Claim = take ownership (reassign-on-claim). Moves a Group Site into My Sites.
  const claimMutation = useMutation({
    mutationFn: (siteId: string) => scheduleService.claimSite(siteId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['my-access'] });
      queryClient.invalidateQueries({ queryKey: ['weekly-plan'] });
      queryClient.invalidateQueries({ queryKey: ['lite-sites'] });
      toast({ title: `Claimed ${data.site_name} — it's yours now` });
    },
    onError: (error: any) => {
      toast({
        title: error?.response?.data?.detail || 'Could not claim site',
        variant: 'destructive',
      });
    },
  });

  // Release = give a site back to the group pool (no longer mine).
  const releaseMutation = useMutation({
    mutationFn: (siteId: string) => scheduleService.unclaimSite(siteId),
    // Optimistically drop the site from My Sites so it disappears on tap.
    // invalidate-then-refetch alone left it on screen until a manual refresh
    // (read-after-write lag through the pooled connection) — #290.
    onMutate: async (siteId: string) => {
      await queryClient.cancelQueries({ queryKey: ['my-access'] });
      const prev = queryClient.getQueryData<MyAccessResponse>(['my-access']);
      queryClient.setQueryData<MyAccessResponse>(['my-access'], (old) =>
        old
          ? { ...old, my_sites: old.my_sites.filter((s) => s.id !== siteId) }
          : old
      );
      return { prev };
    },
    onError: (error: any, _siteId, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['my-access'], ctx.prev);
      toast({
        title: error?.response?.data?.detail || 'Could not release site',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({ title: 'Released to your group' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['my-access'] });
    },
  });

  const total = sitesData?.total ?? 0;

  // Every active site the server matched; the server already filtered, so
  // nothing is dropped here (the old own-sites filter hid group sites too).
  const filteredSites = (sitesData?.items || []).map((site) => ({
    id: site.id,
    name: site.name,
    clientName: site.client_name || 'Unknown Client',
    city: site.city || '',
    state: site.state || '',
    latitude: site.latitude,
    longitude: site.longitude,
    assignedInspectorId: site.assigned_inspector_id,
    assignedInspectorName: site.assigned_inspector_name,
    reachable: site.reachable,
  }));

  const weekDays = getWeekDays();

  return (
    <div className="space-y-4" onClickCapture={rememberOnLeave}>
      {/* No page-level "My Sites" header: the search covers all sites and the
          "My Sites" / "Group Sites" sections below are self-labeling (Jim, 2026-06). */}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search all sites..."
          className="w-full pl-10 pr-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-slate-900 dark:text-white bg-white dark:bg-slate-800 placeholder-slate-500 dark:placeholder-slate-400"
        />
      </div>

      {/* ---- Overdue (day-based) — always shown so the dashboard card lands here ---- */}
      {!searchTerm && !accessLoading && (
        <LiteListSection
          id="overdue"
          title="Overdue Inspections"
          count={overdueSites.length}
          tone="danger"
          icon={<AlertTriangle className="w-4 h-4" />}
        >
          {overdueSites.length === 0 ? (
            <p className="text-sm text-slate-400 dark:text-slate-500">
              Nothing overdue right now.
            </p>
          ) : (
            overdueSites.map((site) => (
              <LiteSiteCard
                key={site.id}
                title={site.name}
                subtitle={siteSubtitle(site)}
                accent="danger"
                to={`/lite/sites/${site.id}`}
                pill={
                  <div className="flex items-center gap-1.5">
                    <TransitionChip phase={site.transition_phase} />
                    {typeof site.days_overdue === 'number' && (
                      <span className="px-1.5 py-0.5 rounded bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 text-[10px] font-bold uppercase flex-shrink-0">
                        {site.days_overdue}d{site.days_overdue >= 7 ? '+' : ''}
                      </span>
                    )}
                  </div>
                }
                actions={
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/lite/inspect/${site.id}`}
                      className="flex-1 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-primary-700 transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      Inspect
                    </Link>
                    <DirectionsLink
                      latitude={site.latitude}
                      longitude={site.longitude}
                    />
                  </div>
                }
              />
            ))
          )}
        </LiteListSection>
      )}

      {/* ---- Rainfall — my rain event inspections still to do ---- */}
      {!searchTerm && !accessLoading && (
        <LiteListSection
          id="rainfall"
          title="Rain Event Inspections"
          count={rainfallSites.length}
          tone="rain"
          icon={<CloudRain className="w-4 h-4" />}
        >
          {rainfallSites.length === 0 ? (
            <p className="text-sm text-slate-400 dark:text-slate-500">
              No rain event inspections to do.
            </p>
          ) : (
            rainfallSites.map((site) => (
              <LiteSiteCard
                key={site.id}
                title={site.name}
                subtitle={siteSubtitle(site)}
                accent="info"
                to={`/lite/sites/${site.id}`}
                pill={
                  <CloudRain className="w-4 h-4 text-sky-500 flex-shrink-0" />
                }
                actions={
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/lite/inspect/${site.id}?rain=1`}
                      className="flex-1 px-3 py-2 bg-sky-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-sky-700 transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      Inspect
                    </Link>
                    <DirectionsLink
                      latitude={site.latitude}
                      longitude={site.longitude}
                    />
                  </div>
                }
              />
            ))
          )}
        </LiteListSection>
      )}

      {/* ---- My Sites (assigned to me) — shown when not searching ---- */}
      {!searchTerm && !accessLoading && (
        <LiteListSection
          id="my-sites"
          title="My Sites"
          count={mySites.length}
          icon={<MapPin className="w-4 h-4" />}
        >
          {mySites.length === 0 ? (
            <p className="text-sm text-slate-400 dark:text-slate-500">
              No sites assigned to you yet.
            </p>
          ) : (
            mySites.map((site) => (
              <LiteSiteCard
                key={site.id}
                title={site.name}
                subtitle={siteSubtitle(site)}
                accent={site.status === 'pending' ? 'warning' : 'default'}
                to={`/lite/sites/${site.id}`}
                pill={
                  <div className="flex items-center gap-1.5">
                    <TransitionChip phase={site.transition_phase} />
                    {site.status === 'pending' && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] font-bold uppercase flex-shrink-0">
                        Pending
                      </span>
                    )}
                  </div>
                }
                actions={
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/lite/inspect/${site.id}`}
                      className="flex-1 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-primary-700 transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      Inspect
                    </Link>
                    <DirectionsLink
                      latitude={site.latitude}
                      longitude={site.longitude}
                    />
                    <button
                      onClick={() => releaseMutation.mutate(site.id)}
                      disabled={releaseMutation.isPending}
                      className="text-[11px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 disabled:opacity-50 flex-shrink-0"
                    >
                      Release
                    </button>
                  </div>
                }
              >
                {site.last_inspection_date && (
                  <p className="text-xs text-slate-400 mt-1">
                    Last: {format(new Date(site.last_inspection_date), 'MMM d')}
                  </p>
                )}
              </LiteSiteCard>
            ))
          )}
        </LiteListSection>
      )}

      {/* ---- Group Sites (claimable) — shown when not searching ---- */}
      {!searchTerm && !accessLoading && (
        <LiteListSection
          id="group-sites"
          title="Group Sites"
          count={groupSites.length}
          icon={<Users className="w-4 h-4" />}
          intro={
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Sites in your group{access && access.groups.length > 1 ? 's' : ''}{' '}
              — claim one to add it to My Sites.
            </p>
          }
        >
          <div className="space-y-2">
            {groupSites.map((site) => (
              <LiteSiteCard
                key={site.id}
                title={site.name}
                subtitle={siteSubtitle(site)}
                accent={site.status === 'pending' ? 'warning' : 'default'}
                to={`/lite/sites/${site.id}`}
                pill={
                  <div className="flex items-center gap-2">
                    <TransitionChip phase={site.transition_phase} />
                    {site.status === 'pending' && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] font-bold uppercase flex-shrink-0">
                        Pending
                      </span>
                    )}
                    <DirectionsLink
                      latitude={site.latitude}
                      longitude={site.longitude}
                      compact
                    />
                    <button
                      onClick={() => claimMutation.mutate(site.id)}
                      disabled={claimMutation.isPending}
                      className="px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors flex-shrink-0"
                    >
                      <Plus className="w-4 h-4" />
                      Claim
                    </button>
                  </div>
                }
              />
            ))}
          </div>
        </LiteListSection>
      )}

      {!searchTerm && accessLoading && <SkeletonList rows={5} />}

      {/* Search Results — only shown when searching */}
      {!searchTerm ? null : isLoading || settling ? (
        <SkeletonList rows={5} />
      ) : (
        <div className="space-y-3">
          {filteredSites.map((site) => {
            const row = searchRowState(site, user?.id);
            return (
              <LiteSiteCard
                key={site.id}
                title={site.name}
                subtitle={siteSubtitle(site)}
                to={row.canOpen ? `/lite/sites/${site.id}` : undefined}
                pill={
                  row.canOpen ? (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  ) : (
                    <span
                      className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap"
                      data-testid="search-not-mine"
                    >
                      {site.assignedInspectorName
                        ? `${site.assignedInspectorName}'s`
                        : 'Unassigned'}
                    </span>
                  )
                }
                actions={
                  <>
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      {row.canOpen ? (
                        <Link
                          to={`/lite/inspect/${site.id}`}
                          className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 active:bg-primary-800 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                        >
                          <Play className="w-4 h-4" />
                          Start Inspection
                        </Link>
                      ) : (
                        <span
                          className="flex-1 self-center text-sm text-slate-500 dark:text-slate-400"
                          data-testid="search-claim-hint"
                        >
                          Not on your list yet — claim it to open it.
                        </span>
                      )}
                      <DirectionsLink
                        latitude={site.latitude}
                        longitude={site.longitude}
                        compact
                        className="self-center"
                      />
                      {row.canClaim && (
                        <button
                          onClick={() => claimMutation.mutate(site.id)}
                          disabled={claimMutation.isPending}
                          title="Claim (make it mine)"
                          className="px-4 py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Claim
                        </button>
                      )}
                      {row.canOpen && (
                        <button
                          onClick={() =>
                            setDayPickerFor(
                              dayPickerFor === site.id ? null : site.id
                            )
                          }
                          className={`px-4 py-3 rounded-lg text-sm font-medium flex items-center justify-center transition-colors ${
                            dayPickerFor === site.id
                              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600'
                          }`}
                        >
                          <CalendarPlus className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Day Picker (inline) */}
                    {dayPickerFor === site.id && (
                      <div className="mt-3">
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                          Pick a day this week:
                        </p>
                        <div className="flex gap-1.5">
                          {weekDays.map((wd) => (
                            <button
                              key={wd.date}
                              onClick={() =>
                                addToPlanMutation.mutate({
                                  siteId: site.id,
                                  date: wd.date,
                                })
                              }
                              disabled={addToPlanMutation.isPending}
                              className="flex-1 py-2 px-1 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-700 dark:hover:bg-primary-900/20 dark:hover:text-primary-300 transition-colors"
                            >
                              {wd.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                }
              />
            );
          })}
        </div>
      )}

      {total > PAGE_SIZE && !isLoading && (
        <div className="flex items-center justify-between pt-1">
          <button
            onClick={() => setCurrentPage((p) => p - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-700 active:bg-slate-100"
          >
            Previous
          </button>
          <span className="text-sm text-slate-500 dark:text-slate-400">
            Page {currentPage} of {Math.ceil(total / PAGE_SIZE)}
          </span>
          <button
            onClick={() => setCurrentPage((p) => p + 1)}
            disabled={currentPage >= Math.ceil(total / PAGE_SIZE)}
            className="px-4 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-700 active:bg-slate-100"
          >
            Next
          </button>
        </div>
      )}

      {!searchTerm &&
        !accessLoading &&
        mySites.length === 0 &&
        groupSites.length === 0 && (
          <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
              <MapPin className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-slate-500 dark:text-slate-400">No sites yet.</p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
              Search above to find and claim sites, or contact your manager.
            </p>
          </div>
        )}
    </div>
  );
}
